import apiClient from "./axios";

/**
 * 航班事务API
 * 处理与航班相关的各类事务（如许可申请等）
 */

// 事务类型映射 - 将前端大写格式转换为后端小写格式
const TRANSACTION_TYPE_MAP = {
    'FUEL_REQUEST': 'fuel_request',
    'PERMIT_REQUEST': 'permit_request',
    'CATERING_ORDER': 'catering_order',
    'FLIGHT_BRIEF': 'flight_brief',
    'CREW_TRANSPORTATION': 'crew_transportation',
    'HOTAC': 'hotac',
};

/**
 * 标准化事务类型
 * @param {string} transactionType 事务类型
 * @returns {string} 标准化后的事务类型
 */
const normalizeTransactionType = (transactionType) => {
    // 如果是大写格式，使用映射表转换
    if (TRANSACTION_TYPE_MAP[transactionType]) {
        return TRANSACTION_TYPE_MAP[transactionType];
    }

    // 如果已经是小写格式，直接返回
    if (Object.values(TRANSACTION_TYPE_MAP).includes(transactionType)) {
        return transactionType;
    }

    // 尝试简单的小写转换作为后备
    return transactionType.toLowerCase();
};
export const flightOperationTransactionApi = {
    /**
     * 获取许可申请列表
     * @param {Object} params 查询参数
     * @returns {Promise<Object>} 许可申请列表 {items: Array, total: number}
     */
    getPermitRequests: async (params = {}) => {
        try {
            const response = await apiClient.get("/v1/flight-permits", { params });
            return response;
        } catch (error) {
            console.error("获取许可申请列表失败:", error);
            throw error;
        }
    },

    /**
     * 获取单个航班的所有许可申请
     * @param {number} flightId 航班ID
     * @param {Object} params 查询参数
     * @returns {Promise<Object>} 许可申请列表
     */
    getFlightPermitRequests: async (flightId, params = {}) => {
        try {
            // 使用v1前缀以符合后端规范
            const response = await apiClient.get(`/v1/flight-operations/${flightId}/permits`, { params });
            return response;
        } catch (error) {
            console.error(`获取航班ID=${flightId}的许可申请失败:`, error);
            throw error;
        }
    },

    /**
     * 获取单个航班的所有燃油申请
     * @param {number} flightId 航班ID
     * @param {Object} params 查询参数
     * @returns {Promise<Object>} 燃油申请列表
     */
    getFlightFuelRequests: async (flightId, params = {}) => {
        try {
            const response = await apiClient.get(`/v1/flight-operations/${flightId}/fuels`, { params });
            return response;
        } catch (error) {
            console.error(`获取航班ID=${flightId}的燃油申请失败:`, error);
            throw error;
        }
    },

    /**
     * 获取单个航班的所有配餐订单
     * @param {number} flightId 航班ID
     * @param {Object} params 查询参数
     * @returns {Promise<Object>} 配餐订单列表
     */
    getFlightCateringOrders: async (flightId, params = {}) => {
        try {
            const response = await apiClient.get(`/v1/flight-operations/${flightId}/caterings`, { params });
            return response;
        } catch (error) {
            console.error(`获取航班ID=${flightId}的配餐订单失败:`, error);
            throw error;
        }
    },

    /**
     * 获取单个航班的所有飞行简报
     * @param {number} flightId 航班ID
     * @param {Object} params 查询参数
     * @returns {Promise<Object>} 飞行简报列表
     */
    getFlightBriefs: async (flightId, params = {}) => {
        try {
            const response = await apiClient.get(`/v1/flight-operations/${flightId}/briefs`, { params });
            return response;
        } catch (error) {
            console.error(`获取航班ID=${flightId}的飞行简报失败:`, error);
            throw error;
        }
    },

    /**
     * 获取许可申请详情
     * @param {number} permitId 许可申请ID
     * @returns {Promise<Object>} 许可申请详情
     */
    getPermitRequest: async (permitId) => {
        try {
            const response = await apiClient.get(`/v1/flight-permits/${permitId}`);
            return response;
        } catch (error) {
            console.error(`获取许可申请ID=${permitId}详情失败:`, error);
            throw error;
        }
    },

    /**
     * 获取燃油申请详情
     * @param {number} fuelId 燃油申请ID
     * @returns {Promise<Object>} 燃油申请详情
     */
    getFuelRequest: async (fuelId) => {
        try {
            const response = await apiClient.get(`/v1/flight-fuels/${fuelId}`);
            return response;
        } catch (error) {
            console.error(`获取燃油申请ID=${fuelId}详情失败:`, error);
            throw error;
        }
    },

    /**
     * 获取配餐订单详情
     * @param {number} cateringId 配餐订单ID
     * @returns {Promise<Object>} 配餐订单详情
     */
    getCateringOrder: async (cateringId) => {
        try {
            const response = await apiClient.get(`/v1/flight-caterings/${cateringId}`);
            return response;
        } catch (error) {
            console.error(`获取配餐订单ID=${cateringId}详情失败:`, error);
            throw error;
        }
    },

    /**
     * 获取飞行简报详情
     * @param {number} briefId 飞行简报ID
     * @returns {Promise<Object>} 飞行简报详情
     */
    getFlightBrief: async (briefId) => {
        try {
            const response = await apiClient.get(`/v1/flight-briefs/${briefId}`);
            return response;
        } catch (error) {
            console.error(`获取飞行简报ID=${briefId}详情失败:`, error);
            throw error;
        }
    },

    /**
     * 创建许可申请
     * @param {number} flightId 航班ID
     * @param {Object} data 许可申请数据
     * @returns {Promise<Object>} 创建结果
     */
    createPermitRequest: async (flightId, data) => {
        try {
            const response = await apiClient.post(`/v1/flight-operations/${flightId}/permits`, data);
            return response;
        } catch (error) {
            console.error(`为航班ID=${flightId}创建许可申请失败:`, error);
            throw error;
        }
    },

    /**
     * 更新许可申请
     * @param {number} permitId 许可申请ID
     * @param {Object} data 更新数据
     * @returns {Promise<Object>} 更新结果
     */
    updatePermitRequest: async (permitId, data) => {
        try {
            const response = await apiClient.put(`/v1/flight-permits/${permitId}`, data);
            return response;
        } catch (error) {
            console.error(`更新许可申请ID=${permitId}失败:`, error);
            throw error;
        }
    },

    /**
     * 通过变更命令创建许可申请
     * @param {number} flightId 航班ID
     * @param {Object} data 许可申请数据
     * @param {Object} operator 操作者信息
     * @param {string} triggerSource 触发源
     * @returns {Promise<Object>} 创建结果
     */
    initiatePermitRequestViaChange: async (flightId, data, operator, triggerSource = "UI") => {
        try {
            const command = {
                flight_operation_id: flightId,
                change_type: "INITIATE_PERMIT_REQUEST",
                payload: data,
                operator: operator,
                trigger_source: triggerSource,
                remarks: data.remarks,
            };

            const response = await apiClient.post(`/v1/flight-operations/changes`, command);
            return response;
        } catch (error) {
            console.error(`通过变更命令创建许可申请失败:`, error);
            throw error;
        }
    },

    /**
     * 通过变更命令更新许可申请状态
     * @param {number} permitId 许可申请ID
     * @param {string} status 新状态
     * @param {Object} data 额外数据
     * @param {Object} operator 操作者信息
     * @param {string} triggerSource 触发源
     * @returns {Promise<Object>} 更新结果
     */
    updatePermitStatusViaChange: async (permitId, status, data = {}, operator, triggerSource = "UI") => {
        try {
            const command = {
                flight_operation_id: data.flight_id, // 需要航班ID
                change_type: "UPDATE_PERMIT_STATUS",
                payload: {
                    permit_request_id: permitId,
                    status: status,
                    remarks: data.remarks,
                    details: data.details,
                },
                operator: operator,
                trigger_source: triggerSource,
                remarks: data.remarks,
            };

            const response = await apiClient.post(`/v1/flight-operations/changes`, command);
            return response;
        } catch (error) {
            console.error(`通过变更命令更新许可申请状态失败:`, error);
            throw error;
        }
    },

    /**
     * 获取航班的事务列表（通用）
     * @param {number} flightId 航班ID
     * @param {string} transactionType 事务类型
     * @param {Object} params 查询参数
     * @returns {Promise<Object>} 事务列表
     */
    getFlightTransactions: async (flightId, transactionType, params = {}) => {
        try {
            const transactionEndpoint = getTransactionEndpoint(transactionType);
            const response = await apiClient.get(`/flight-operations/${flightId}/${transactionEndpoint}`, { params });
            return response;
        } catch (error) {
            console.error(`获取航班ID=${flightId}的${transactionType}事务失败:`, error);
            throw error;
        }
    },

    /**
     * 通过变更命令创建事务的通用方法
     * 用于统一处理所有事务类型的创建操作
     *
     * @param {Object} command 变更命令对象，包括flight_operation_id, change_type, payload等
     * @returns {Promise<Object>} 创建结果
     */
    createTransactionViaChange: async (command) => {
        try {
            const response = await apiClient.post(`/v1/flight-operations/changes`, command);
            return response;
        } catch (error) {
            console.error(`通过变更命令创建事务失败 (Type: ${command?.change_type}):`, error);
            throw error;
        }
    },

    /**
     * 通过变更命令更新事务（通用）
     * @param {Object} command 变更命令
     * @returns {Promise<Object>} 更新结果
     */
    updateTransactionViaChange: async (command) => {
        try {
            const response = await apiClient.post(`/v1/flight-operations/changes`, command);
            return response;
        } catch (error) {
            console.error(`通过变更命令更新事务失败:`, error);
            throw error;
        }
    },

    /**
     * 获取单个航班的所有机组接送记录
     * @param {number} flightId 航班ID
     * @param {Object} params 查询参数
     * @returns {Promise<Object>} 机组接送记录列表
     */
    getFlightCrewTransportations: async (flightId, params = {}) => {
        try {
            const response = await apiClient.get(`/v1/flight-operations/${flightId}/crew-transportations`, { params });
            return response;
        } catch (error) {
            console.error(`获取航班ID=${flightId}的机组接送记录失败:`, error);
            throw error;
        }
    },

    /**
     * 获取单个航班的所有酒店住宿记录
     * @param {number} flightId 航班ID
     * @param {Object} params 查询参数
     * @returns {Promise<Object>} 酒店住宿记录列表
     */
    getFlightHotacs: async (flightId, params = {}) => {
        try {
            const response = await apiClient.get(`/v1/flight-operations/${flightId}/hotacs`, { params });
            return response;
        } catch (error) {
            console.error(`获取航班ID=${flightId}的酒店住宿记录失败:`, error);
            throw error;
        }
    },

    /**
     * 获取机组接送记录详情
     * @param {number} id 机组接送记录ID
     * @returns {Promise<Object>} 机组接送记录详情
     */
    getCrewTransportation: async (id) => {
        try {
            const response = await apiClient.get(`/v1/flight-crew-transportations/${id}`);
            return response;
        } catch (error) {
            console.error(`获取机组接送记录ID=${id}详情失败:`, error);
            throw error;
        }
    },

    /**
     * 获取酒店住宿记录详情
     * @param {number} id 酒店住宿记录ID
     * @returns {Promise<Object>} 酒店住宿记录详情
     */
    getHotac: async (id) => {
        try {
            const response = await apiClient.get(`/v1/flight-hotacs/${id}`);
            return response;
        } catch (error) {
            console.error(`获取酒店住宿记录ID=${id}详情失败:`, error);
            throw error;
        }
    },

    /**
     * 创建机组接送记录
     * @param {number} flightId 航班ID
     * @param {Object} data 机组接送记录数据
     * @returns {Promise<Object>} 创建结果
     */
    createCrewTransportation: async (flightId, data) => {
        try {
            const response = await apiClient.post(`/v1/flight-operations/${flightId}/crew-transportations`, data);
            return response;
        } catch (error) {
            console.error(`为航班ID=${flightId}创建机组接送记录失败:`, error);
            throw error;
        }
    },

    /**
     * 创建酒店住宿记录
     * @param {number} flightId 航班ID
     * @param {Object} data 酒店住宿记录数据
     * @returns {Promise<Object>} 创建结果
     */
    createHotac: async (flightId, data) => {
        try {
            const response = await apiClient.post(`/v1/flight-operations/${flightId}/hotacs`, data);
            return response;
        } catch (error) {
            console.error(`为航班ID=${flightId}创建酒店住宿记录失败:`, error);
            throw error;
        }
    },

    /**
     * 更新机组接送记录
     * @param {number} id 机组接送记录ID
     * @param {Object} data 更新数据
     * @returns {Promise<Object>} 更新结果
     */
    updateCrewTransportation: async (id, data) => {
        try {
            const response = await apiClient.put(`/v1/flight-crew-transportations/${id}`, data);
            return response;
        } catch (error) {
            console.error(`更新机组接送记录ID=${id}失败:`, error);
            throw error;
        }
    },

    /**
     * 更新酒店住宿记录
     * @param {number} id 酒店住宿记录ID
     * @param {Object} data 更新数据
     * @returns {Promise<Object>} 更新结果
     */
    updateHotac: async (id, data) => {
        try {
            const response = await apiClient.put(`/v1/flight-hotacs/${id}`, data);
            return response;
        } catch (error) {
            console.error(`更新酒店住宿记录ID=${id}失败:`, error);
            throw error;
        }
    },

    /**
     * 获取事务历史链路
     * @param {string} transactionType 事务类型 (支持大写格式如 FUEL_REQUEST 或小写格式如 fuel_request)
     * @param {number} transactionId 事务ID
     * @returns {Promise<Object>} 事务历史链路
     */
    getTransactionHistoryChain: async (transactionType, transactionId) => {
        try {
            // 标准化事务类型
            const normalizedType = normalizeTransactionType(transactionType);
            const response = await apiClient.get(`/v1/transactions/${normalizedType}/${transactionId}/history`);
            return response;
        } catch (error) {
            console.error(`获取事务历史链路失败 (Type: ${transactionType}, ID: ${transactionId}):`, error);
            throw error;
        }
    },

    /**
     * 获取航班的所有事务历史链路
     * @param {number} flightId 航班ID
     * @returns {Promise<Object>} 航班所有事务历史链路
     */
    getFlightTransactionsHistory: async (flightId) => {
        try {
            const response = await apiClient.get(`/v1/flights/${flightId}/transactions/history`);
            return response;
        } catch (error) {
            console.error(`获取航班事务历史失败 (Flight ID: ${flightId}):`, error);
            throw error;
        }
    },

    /**
     * 确认事务迁移
     * @param {string} transactionType 事务类型 (支持大写格式如 FUEL_REQUEST 或小写格式如 fuel_request)
     * @param {number} transactionId 事务ID
     * @returns {Promise<Object>} 确认结果
     */
    confirmTransactionMigration: async (transactionType, transactionId) => {
        try {
            // 标准化事务类型
            const normalizedType = normalizeTransactionType(transactionType);
            const response = await apiClient.post(`/v1/transactions/${normalizedType}/${transactionId}/confirm-migration`);
            return response;
        } catch (error) {
            console.error(`确认事务迁移失败 (Type: ${transactionType}, ID: ${transactionId}):`, error);
            throw error;
        }
    },

    /**
     * 拒绝事务迁移
     * @param {string} transactionType 事务类型 (支持大写格式如 FUEL_REQUEST 或小写格式如 fuel_request)
     * @param {number} transactionId 事务ID
     * @returns {Promise<Object>} 拒绝结果
     */
    rejectTransactionMigration: async (transactionType, transactionId) => {
        try {
            // 标准化事务类型
            const normalizedType = normalizeTransactionType(transactionType);
            const response = await apiClient.post(`/v1/transactions/${normalizedType}/${transactionId}/reject-migration`);
            return response;
        } catch (error) {
            console.error(`拒绝事务迁移失败 (Type: ${transactionType}, ID: ${transactionId}):`, error);
            throw error;
        }
    },
};

/**
 * 获取事务类型对应的API端点
 * @param {string} transactionType 事务类型
 * @returns {string} API端点
 */
function getTransactionEndpoint(transactionType) {
    const endpointMap = {
        FUEL_REQUEST: "fuels",
        PERMIT_REQUEST: "permits",
        CATERING_ORDER: "caterings",
        FLIGHT_BRIEF: "briefs",
        CREW_TRANSPORTATION: "crew-transportations",
        HOTAC: "hotacs",
    };

    return endpointMap[transactionType] || "";
}

export default flightOperationTransactionApi;
